import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { LogOut, Mail, Building2, MessageSquare, Calendar } from 'lucide-react';
import { secureStorage } from '../utils/secureStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HTTP Status Codes
const HTTP_UNAUTHORIZED = 401;

export const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLogout = useCallback(() => {
    secureStorage.removeItem('admin_token');
    secureStorage.removeItem('admin_email');
    toast.success('Sesión cerrada');
    navigate('/admin');
  }, [navigate, toast]);
  // Note: secureStorage is a static import, not a reactive dependency

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchContacts = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setContacts(response.data);
    } catch (error) {
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        toast.error('Sesión expirada');
        handleLogout();
      } else {
        toast.error('Error al cargar contactos');
      }
    } finally {
      setIsLoading(false);
    }
  }, [handleLogout, toast, setContacts, setIsLoading]);
  // Note: axios, API, HTTP_UNAUTHORIZED are static constants

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = secureStorage.getItem('admin_token');
    const email = secureStorage.getItem('admin_email');
    
    if (!token) {
      navigate('/admin');
      return;
    }

    setAdminEmail(email);
    fetchContacts(token);
  }, [navigate, fetchContacts, setAdminEmail]);
  // Note: secureStorage is a static utility, token and email are local to this effect

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#152437] flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#152437]">
      {/* Header */}
      <header className="bg-[#111827] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_strategic-studio/artifacts/83cepfxw_Quirofano%20Final%20Variantes-03.svg"
              alt="Quirófano Creativo"
              className="h-10"
            />
            <div>
              <h1 className="text-xl font-light text-white">
                Panel de <span className="text-[#1aa6b6] font-bold">Administración</span>
              </h1>
              <p className="text-sm text-gray-400">{adminEmail}</p>
            </div>
          </div>
          
          <Button
            onClick={handleLogout}
            className="bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[#111827] border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Total Contactos</p>
                <p className="text-3xl font-bold text-white">{contacts.length}</p>
              </div>
              <div className="w-12 h-12 bg-[#1aa6b6]/10 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#1aa6b6]" />
              </div>
            </div>
          </Card>

          <Card className="bg-[#111827] border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Este Mes</p>
                <p className="text-3xl font-bold text-white">
                  {contacts.filter(c => {
                    const contactDate = new Date(c.timestamp);
                    const now = new Date();
                    return contactDate.getMonth() === now.getMonth() && 
                           contactDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#6eb9e1]/10 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#6eb9e1]" />
              </div>
            </div>
          </Card>

          <Card className="bg-[#111827] border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm mb-1">Con Empresa</p>
                <p className="text-3xl font-bold text-white">
                  {contacts.filter(c => c.company).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#10B981]" />
              </div>
            </div>
          </Card>
        </div>

        {/* Contacts Table */}
        <Card className="bg-[#111827] border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-2xl font-light text-white">
              Contactos <span className="text-[#1aa6b6] font-bold">Recibidos</span>
            </h2>
          </div>

          <div className="overflow-x-auto">
            {contacts.length === 0 ? (
              <div className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay contactos aún</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-gray-300">Fecha</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-300">Nombre</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-300">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-300">Empresa</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-300">Mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact, index) => (
                    <tr 
                      key={contact.id} 
                      className="border-t border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="p-4 text-sm text-gray-400">
                        {formatDate(contact.timestamp)}
                      </td>
                      <td className="p-4 text-white font-medium">
                        {contact.name}
                      </td>
                      <td className="p-4">
                        <a 
                          href={`mailto:${contact.email}`}
                          className="text-[#1aa6b6] hover:text-[#6eb9e1] transition-colors"
                        >
                          {contact.email}
                        </a>
                      </td>
                      <td className="p-4 text-gray-300">
                        {contact.company || '-'}
                      </td>
                      <td className="p-4 text-gray-300 max-w-md">
                        <div className="line-clamp-2" title={contact.message}>
                          {contact.message}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
